import { 
  collection, 
  doc, 
  query, 
  orderBy, 
  limit, 
  onSnapshot, 
  updateDoc, 
  deleteDoc, 
  writeBatch, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase';
import { InAppNotification } from '../models/NotificationModels';
import { UserProfile } from '../models/FirestoreModels';

export interface DispatchMentionParams {
  actor: {
    id: string;
    displayName: string;
    photoUrl?: string;
  };
  text: string;
  snippet?: string;
  type: 'mention_post' | 'mention_comment' | 'event_comment_mention' | string;
  targetType: 'post' | 'comment' | 'event' | string;
  targetId: string;
  targetUrl: string;
  mentionedMembers: UserProfile[];
}

/**
 * Subscribes to real-time notifications for a specific member.
 */
export function subscribeToNotifications(
  userId: string,
  callback: (notifications: InAppNotification[]) => void,
  onError?: (error: any) => void
): () => void {
  if (!userId) {
    callback([]);
    return () => {};
  }

  const notifsRef = collection(db, 'members', userId, 'notifications');
  const q = query(notifsRef, orderBy('createdAt', 'desc'), limit(50));

  const mapSnapshot = (snapshot: any): InAppNotification[] => {
    return snapshot.docs.map((docSnap: any) => {
      const data = docSnap.data();
      let createdAt = data.createdAt;
      if (createdAt && typeof createdAt.toDate === 'function') {
        createdAt = createdAt.toDate().toISOString();
      } else if (createdAt && typeof createdAt === 'object' && createdAt.seconds) {
        createdAt = new Date(createdAt.seconds * 1000).toISOString();
      }
      return {
        id: docSnap.id,
        recipientId: data.recipientId || userId,
        actorId: data.actorId || '',
        actorName: data.actorName || 'Member',
        actorAvatarUrl: data.actorAvatarUrl || '',
        type: data.type || 'mention_comment',
        title: data.title || 'New notification',
        snippet: data.snippet || '',
        targetType: data.targetType || 'post',
        targetId: data.targetId || '',
        targetUrl: data.targetUrl || '/',
        isRead: !!data.isRead,
        createdAt: createdAt || new Date().toISOString(),
      };
    }).sort((a: InAppNotification, b: InAppNotification) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  };

  let fallbackUnsubscribe: (() => void) | null = null;
  const primaryUnsubscribe = onSnapshot(
    q,
    (snapshot) => {
      callback(mapSnapshot(snapshot));
    },
    (err) => {
      console.warn('[NotificationCenter] Primary query failed, attempting unindexed fallback:', err);
      try {
        const fallbackQ = query(notifsRef, limit(50));
        fallbackUnsubscribe = onSnapshot(
          fallbackQ,
          (fbSnap) => {
            callback(mapSnapshot(fbSnap));
          },
          (fbErr) => {
            console.error('[NotificationCenter] Fallback notification subscription failed:', fbErr);
            if (onError) onError(fbErr);
            callback([]);
          }
        );
      } catch (fallbackEx) {
        if (onError) onError(err);
        callback([]);
      }
    }
  );

  return () => {
    primaryUnsubscribe();
    if (fallbackUnsubscribe) {
      fallbackUnsubscribe();
    }
  };
}

/**
 * Mark a single notification as read.
 */
export async function markNotificationAsRead(userId: string, notificationId: string): Promise<void> {
  if (!userId || !notificationId) return;
  try {
    const notifRef = doc(db, 'members', userId, 'notifications', notificationId);
    await updateDoc(notifRef, { isRead: true });
  } catch (err) {
    console.error('[NotificationCenter] Error marking notification as read:', err);
  }
}

/**
 * Mark all notifications as read for a user.
 */
export async function markAllNotificationsAsRead(userId: string, unreadIds: string[]): Promise<void> {
  if (!userId || !unreadIds || unreadIds.length === 0) return;
  try {
    const batch = writeBatch(db);
    unreadIds.forEach((id) => {
      const notifRef = doc(db, 'members', userId, 'notifications', id);
      batch.update(notifRef, { isRead: true });
    });
    await batch.commit();
  } catch (err) {
    console.error('[NotificationCenter] Error marking all notifications as read:', err);
  }
}

/**
 * Delete a notification.
 */
export async function deleteNotification(userId: string, notificationId: string): Promise<void> {
  if (!userId || !notificationId) return;
  try {
    const notifRef = doc(db, 'members', userId, 'notifications', notificationId);
    await deleteDoc(notifRef);
  } catch (err) {
    console.error('[NotificationCenter] Error deleting notification:', err);
  }
}

export interface MatchedMention {
  raw: string;
  matchedName: string;
  member?: UserProfile;
  startIndex: number;
  endIndex: number;
}

export interface MemberAlias {
  alias: string;
  lowerAlias: string;
  member: UserProfile;
  length: number;
}

const DELIMITERS = new Set([' ', '\t', '\n', '\r', ',', '.', '!', '?', ';', ':', ')', ']', '"', "'", '/', '\\', '-', '\u00A0']);

/**
 * Builds candidate aliases for members sorted by length descending.
 */
export function buildMemberAliases(allMembers: UserProfile[]): MemberAlias[] {
  const aliases: MemberAlias[] = [];
  const seen = new Set<string>();

  for (const m of allMembers) {
    const memberId = m.id || (m as any).uid || (m as any).userId || '';
    const rawCandidates: (string | undefined | null)[] = [
      m.displayName,
      m.nickname,
      m.firstName && m.lastName ? `${m.firstName} ${m.lastName}` : '',
      m.firstName && m.firstName.length >= 2 ? m.firstName : '',
    ];

    // Extract first word from multi-word displayName (e.g. "John" from "John Doe")
    if (m.displayName && m.displayName.includes(' ')) {
      const parts = m.displayName.trim().split(/\s+/);
      if (parts[0] && parts[0].length >= 2) {
        rawCandidates.push(parts[0]);
      }
      // Compact no-spaces variations: e.g. "JohnDoe", "John_Doe", "John-Doe"
      rawCandidates.push(m.displayName.replace(/\s+/g, ''));
      rawCandidates.push(m.displayName.replace(/\s+/g, '_'));
      rawCandidates.push(m.displayName.replace(/\s+/g, '-'));
    }

    if (m.nickname && m.nickname.includes(' ')) {
      rawCandidates.push(m.nickname.replace(/\s+/g, ''));
      rawCandidates.push(m.nickname.replace(/\s+/g, '_'));
    }

    if (m.email && m.email.includes('@')) {
      const emailPrefix = m.email.split('@')[0];
      if (emailPrefix && emailPrefix.length >= 3) {
        rawCandidates.push(emailPrefix);
      }
    }

    for (const c of rawCandidates) {
      if (!c) continue;
      const clean = c.trim();
      if (!clean) continue;
      const lower = clean.toLowerCase();
      const key = `${memberId}_${lower}`;
      if (!seen.has(key)) {
        seen.add(key);
        aliases.push({
          alias: clean,
          lowerAlias: lower,
          member: m,
          length: clean.length,
        });
      }
    }
  }

  // Sort by length descending so multi-word names take priority over single-word prefixes
  return aliases.sort((a, b) => b.length - a.length);
}

/**
 * Finds all @mention occurrences in text and maps them to members.
 */
export function findMentionsInText(text: string, allMembers: UserProfile[]): MatchedMention[] {
  if (!text) return [];
  const aliases = buildMemberAliases(allMembers);
  const results: MatchedMention[] = [];

  for (let i = 0; i < text.length; i++) {
    if (text[i] !== '@') continue;

    // Check boundary before '@'
    if (i > 0) {
      const prevChar = text[i - 1];
      if (!DELIMITERS.has(prevChar) && prevChar !== '(' && prevChar !== '[') {
        continue;
      }
    }

    const remainingText = text.substring(i + 1);
    const lowerRemaining = remainingText.toLowerCase();

    let matched = false;
    for (const alias of aliases) {
      if (lowerRemaining.startsWith(alias.lowerAlias)) {
        const nextCharIndex = alias.length;
        if (
          nextCharIndex >= remainingText.length ||
          DELIMITERS.has(remainingText[nextCharIndex])
        ) {
          const matchedName = remainingText.substring(0, alias.length);
          results.push({
            raw: `@${matchedName}`,
            matchedName,
            member: alias.member,
            startIndex: i,
            endIndex: i + 1 + alias.length,
          });
          matched = true;
          i += alias.length;
          break;
        }
      }
    }

    // Fallback: match generic @word if no registered member name matches
    if (!matched) {
      const fallbackMatch = remainingText.match(/^([A-Za-z0-9_\.\-]+)/);
      if (fallbackMatch) {
        const word = fallbackMatch[1];
        const cleanWord = word.replace(/[.,!?;:]+$/, '');
        if (cleanWord) {
          results.push({
            raw: `@${cleanWord}`,
            matchedName: cleanWord,
            startIndex: i,
            endIndex: i + 1 + cleanWord.length,
          });
          i += cleanWord.length;
        }
      }
    }
  }

  return results;
}

/**
 * Extract mentioned members from raw text (e.g. "@John", "@SarahC", "@John Doe").
 */
export function extractMentionedMembers(text: string, allMembers: UserProfile[]): UserProfile[] {
  if (!text || !allMembers || allMembers.length === 0) return [];
  const mentions = findMentionsInText(text, allMembers);
  const uniqueMembers = new Map<string, UserProfile>();
  for (const m of mentions) {
    if (m.member) {
      const id = m.member.id || (m.member as any).uid || (m.member as any).userId;
      if (id) {
        uniqueMembers.set(id, m.member);
      }
    }
  }
  return Array.from(uniqueMembers.values());
}

/**
 * Dispatches notifications to all mentioned users in a batch.
 */
export async function dispatchMentionNotifications(params: DispatchMentionParams): Promise<void> {
  const { actor, text, snippet, type, targetType, targetId, targetUrl, mentionedMembers } = params;

  if (!mentionedMembers || mentionedMembers.length === 0) return;

  try {
    const batch = writeBatch(db);
    let count = 0;

    const cleanSnippet = snippet || (text.length > 120 ? text.substring(0, 117) + '...' : text);

    for (const member of mentionedMembers) {
      const targetMemberId = member.id || (member as any).uid || (member as any).userId;
      if (!targetMemberId) continue;

      const isSelfMention = targetMemberId === actor.id;
      const notifRef = doc(collection(db, 'members', targetMemberId, 'notifications'));

      let title = isSelfMention
        ? 'You mentioned yourself in a comment'
        : `${actor.displayName} mentioned you in a comment`;

      if (type === 'mention_post') {
        title = isSelfMention
          ? 'You mentioned yourself in a post'
          : `${actor.displayName} mentioned you in a post`;
      } else if (type === 'event_comment_mention') {
        title = isSelfMention
          ? 'You mentioned yourself on an event'
          : `${actor.displayName} mentioned you on an event`;
      }

      batch.set(notifRef, {
        recipientId: targetMemberId,
        actorId: actor.id,
        actorName: actor.displayName,
        actorAvatarUrl: actor.photoUrl || '',
        type: isSelfMention ? 'mention_self' : type,
        title,
        snippet: cleanSnippet,
        targetType,
        targetId,
        targetUrl,
        isRead: false,
        createdAt: serverTimestamp(),
      });
      count++;
    }

    // Also dispatch an author confirmation notification if other members were mentioned
    const otherMembers = mentionedMembers.filter(m => {
      const mId = m.id || (m as any).uid || (m as any).userId;
      return mId && mId !== actor.id;
    });

    if (otherMembers.length > 0 && actor.id) {
      const names = otherMembers
        .map(m => m.displayName || m.nickname || m.firstName || 'Member')
        .slice(0, 3)
        .join(', ');
      const overflow = otherMembers.length > 3 ? ` +${otherMembers.length - 3} more` : '';
      const authorNotifRef = doc(collection(db, 'members', actor.id, 'notifications'));
      const authorTitle = type === 'mention_post'
        ? `You mentioned ${names}${overflow} in your post`
        : `You mentioned ${names}${overflow} in your comment`;

      batch.set(authorNotifRef, {
        recipientId: actor.id,
        actorId: actor.id,
        actorName: actor.displayName,
        actorAvatarUrl: actor.photoUrl || '',
        type,
        title: authorTitle,
        snippet: cleanSnippet,
        targetType,
        targetId,
        targetUrl,
        isRead: false,
        createdAt: serverTimestamp(),
      });
      count++;
    }

    if (count > 0) {
      await batch.commit();
      console.log(`[NotificationCenter] Successfully dispatched ${count} mention notification(s).`);
    }
  } catch (err) {
    console.error('[NotificationCenter] Failed to dispatch mention notifications:', err);
  }
}

/**
 * Retroactively scans recent feed posts for mentions involving the current user
 * (either as author or as tagged member) and ensures notifications exist.
 */
export async function syncRecentPostMentions(currentUser: { uid: string; displayName?: string; photoUrl?: string }): Promise<void> {
  if (!currentUser?.uid) return;
  const sessionKey = `acs_mention_sync_${currentUser.uid}`;
  const lastSync = sessionStorage.getItem(sessionKey);
  if (lastSync && Date.now() - parseInt(lastSync, 10) < 3 * 60 * 1000) {
    return;
  }

  try {
    sessionStorage.setItem(sessionKey, Date.now().toString());

    const { feedApi } = await import('./feedApi');
    const recentPosts = await feedApi.getFeedTimeline(10);
    if (!recentPosts || recentPosts.length === 0) return;

    const postsWithMentions = recentPosts.filter(p => 
      (p.headline && p.headline.includes('@')) || 
      (p.body && p.body.includes('@'))
    );
    if (postsWithMentions.length === 0) return;

    const { getMembers } = await import('../firebase');
    const allMembers = await getMembers();
    if (!allMembers || allMembers.length === 0) return;

    const { getDocs } = await import('firebase/firestore');
    const notifsRef = collection(db, 'members', currentUser.uid, 'notifications');
    const existingSnap = await getDocs(query(notifsRef, limit(50)));
    const existingTargetIds = new Set(existingSnap.docs.map(d => d.data().targetId));

    for (const post of postsWithMentions) {
      if (existingTargetIds.has(post.id)) continue;

      const postText = `${post.headline || ''} ${post.body || ''}`;
      const mentioned = extractMentionedMembers(postText, allMembers);
      if (mentioned.length === 0) continue;

      const isMentioned = mentioned.some(m => (m.id || (m as any).uid) === currentUser.uid);
      const isAuthor = post.primaryActors?.some(a => a.id === currentUser.uid);

      if (isMentioned || isAuthor) {
        const actor = post.primaryActors?.[0] || {
          id: currentUser.uid,
          name: currentUser.displayName || 'Admin',
          avatarUrl: currentUser.photoUrl || '',
        };

        await dispatchMentionNotifications({
          actor: {
            id: actor.id,
            displayName: actor.name,
            photoUrl: actor.avatarUrl,
          },
          text: post.body || post.headline,
          snippet: post.headline && post.body ? `${post.headline}: ${post.body}` : (post.headline || post.body || ''),
          type: 'mention_post',
          targetType: 'post',
          targetId: post.id,
          targetUrl: '/feed',
          mentionedMembers: mentioned,
        });
        existingTargetIds.add(post.id);
      }
    }
  } catch (err) {
    console.warn('[NotificationCenter] syncRecentPostMentions error:', err);
  }
}
